import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useUserData } from '../../hooks/useUserData';

interface SidebarProps {
  unreadCount: number;
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
  isMobile?: boolean;
  onLinkClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  unreadCount, 
  isCollapsed, 
  setIsCollapsed, 
  isMobile = false, 
  onLinkClick 
}) => {
  const { user: userData } = useUserData();
  const auth = useAuth();
  const navigate = useNavigate();

  return (
    <div className={`relative ${isCollapsed ? 'w-20' : 'w-64'} transition-all duration-300 ease-in-out`}>
      <aside className="bg-white border-r border-gray-200 shadow-sm sticky top-0 h-screen overflow-y-auto flex flex-col">
        <div className={`py-8 px-6 border-b border-gray-200 ${isCollapsed ? 'px-2' : ''}`}>
          <h2 className={`text-blue-600 text-2xl font-bold mb-6 ${isCollapsed ? 'text-center' : ''}`}>
            {isCollapsed ? 'SI' : 'SinalInvest'}
          </h2>
          {!isCollapsed && auth.isAuthenticated && userData && (
            <div className="text-sm text-gray-600">
              <div className="font-semibold text-gray-800 text-base mb-1">{userData.name || 'Usuário'}</div>
              <div className="mb-2">{userData.email || 'Sem email'}</div>
              {userData.phoneNumber && (
                <div className="mb-4">📱 {userData.phoneNumber}</div>
              )}
              <button
                onClick={() => navigate('/auth-debug')}
                className="text-xs bg-gray-100 border border-gray-200 rounded py-1 px-2 cursor-pointer text-gray-600 transition-all duration-200 ease-in-out hover:bg-gray-200 hover:text-gray-800"
                title="Ver informações de autenticação"
              >
                🔍 Debug Auth
              </button>
            </div>
          )}
        </div>

        <nav className="py-4 flex-1">
          <NavLink 
            to="/dashboard" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>}
                <span className="text-xl w-6 text-center">📊</span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Dashboard</span>
              </>
            )}
          </NavLink>
          <NavLink 
            to="/empresas" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>}
                <span className="text-xl w-6 text-center">🏢</span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Minhas empresas</span>
              </>
            )}
          </NavLink>
          <NavLink 
            to="/notificacoes" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>}
                <span className="text-xl w-6 text-center">🔔</span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Notificações</span>
                {!isCollapsed && unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-semibold py-1 px-2 rounded-full ml-auto">
                    {unreadCount}
                  </span>
                )}
              </>
            )}
          </NavLink>
          <NavLink 
            to="/configuracoes" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>}
                <span className="text-xl w-6 text-center">⚙️</span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Configurações</span>
              </>
            )}
          </NavLink>
          <NavLink 
            to="/assinatura" 
            onClick={onLinkClick}
            className={({ isActive }) =>
            `flex items-center w-full py-4 text-left text-base font-medium cursor-pointer transition-all duration-200 ease-in-out relative no-underline ${
              isCollapsed ? 'px-6 justify-center' : 'px-6 space-x-3'
            } ${
              isActive
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`
          }>
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>}
                <span className="text-xl w-6 text-center">💳</span>
                <span className={isCollapsed ? 'hidden' : 'block'}>Assinatura</span>
              </>
            )}
          </NavLink>
        </nav>
        {!isMobile && (
          <div className="p-6 mt-auto">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <span className="text-2xl">{isCollapsed ? '▶️' : '◀️'}</span>
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default Sidebar;
